import {
  ButtonBase,
  FocusScope,
  TooltipContainer,
  TooltipContainerProps,
  TooltipPopover,
  useHover,
} from '@sparemin/blockhead';
import React, { useState } from 'react';
import Tooltip from 'components/Tooltip';
import { block } from './utils';

type PickedTooltipContainerProps = Pick<
  TooltipContainerProps,
  'offset' | 'placement' | 'strategy'
>;

interface ToolbarPopoverButtonProps extends PickedTooltipContainerProps {
  children: React.ReactNode;
  isEnabled: boolean;
  disableControls?: boolean;
  /**
   * Strips the default toolbar-button styling from the trigger so it can host
   * a custom-sized element such as a small color swatch. The popover behavior
   * stays the same.
   */
  minimal?: boolean;
  onClickTrigger: () => void;
  popoverContent: React.ReactNode;
  buttonTooltipContent?: React.ReactNode;
}

const ToolbarPopoverButton: React.FunctionComponent<ToolbarPopoverButtonProps> = props => {
  const {
    children,
    isEnabled,
    offset = 0,
    onClickTrigger,
    placement = 'top',
    popoverContent,
    strategy,
    disableControls = false,
    minimal = false,
    buttonTooltipContent,
  } = props;

  // Button hover is necessary because the tooltip will ignore the pointer
  // entry event when the button is clicked preventing is open to be triggered
  const { hoverProps, isHovered: buttonIsHovered } = useHover({});
  const [isContentVisible, setIsContentVisible] = useState(false);

  return (
    <TooltipContainer
      delay={0}
      offset={offset}
      placement={placement}
      contents={({ state: { isOpen } }) => {
        const visible =
          isEnabled && (buttonIsHovered || isOpen) && !disableControls;

        return (
          visible && (
            <TooltipPopover className={block('toolbar-popover')}>
              <FocusScope autoFocus>{popoverContent}</FocusScope>
            </TooltipPopover>
          )
        );
      }}
      strategy={strategy}
      onOpenChange={setIsContentVisible}
    >
      <div className={block('toolbar-popover-trigger-wrapper')}>
        <Tooltip
          content={buttonTooltipContent}
          placement="top"
          show={
            (isContentVisible && isEnabled) ||
            !buttonIsHovered ||
            !buttonTooltipContent
              ? false
              : undefined
          }
        >
          <ButtonBase
            {...hoverProps}
            as="div"
            className={block('toolbar-popover-trigger', {
              disabled: disableControls,
              minimal,
            })}
            isDisabled={disableControls}
            onPress={onClickTrigger}
          >
            {children}
          </ButtonBase>
        </Tooltip>
      </div>
    </TooltipContainer>
  );
};

export default ToolbarPopoverButton;
